import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";
import { getItSalesPartnerOemUsers } from "../../redux/actions/itSalesActions";
import Pagination from "../../../helper/Pagination";
import "../../styles/InputSheet.css";
import { IoIosArrowForward } from "react-icons/io";

const ItEndUserOfOem = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const oemPartner = location.state?.oemPartner;

  const {
    oemUsers = [],
    oemUsersLoading = false,
    oemUsersPagination = {},
  } = useSelector((state) => state.itSales || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("active");

  const fetchOemUsers = (page = 1, search = "", status = "active") => {
    if (!oemPartner?._id) return;

    dispatch(
      getItSalesPartnerOemUsers({
        oemId: oemPartner._id,
        page,
        limit: 10,
        search,
        status,
      })
    );
  };

  useEffect(() => {
    if (oemPartner?._id) {
      fetchOemUsers(1, "", statusFilter);
    } else {
      navigate(-1);
    }
  }, [oemPartner, navigate, statusFilter]);

  useEffect(() => {
    if (oemPartner?._id) {
      fetchOemUsers(currentPage, searchTerm, statusFilter);
    }
  }, [currentPage, searchTerm, statusFilter, oemPartner]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
      setSearchTerm(value);
      setCurrentPage(1);
    } else if (searchTerm.length > 0) {
      setSearchTerm(value);
      setCurrentPage(1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="form-header">
          <div className="d-flex align-items-center">
            <FiArrowLeft
              className="cursor-pointer me-3"
              style={{ fontSize: "1.5rem" }}
              onClick={() => navigate(-1)}
              title="Go Back"
            />
            <div className="d-flex align-items-center">
              {oemPartner?.logo && (
                <img
                  src={oemPartner.logo}
                  alt={oemPartner.companyName}
                  className="me-3"
                  style={{
                    height: "40px",
                    width: "40px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    backgroundColor: "#c5c7caff",
                  }}
                />
              )}
              <div>
                <h2 className="mb-0">
                  {oemPartner?.companyName || "End Users"}
                  <span className="text-secondary">
                    <IoIosArrowForward />
                  </span>
                  End Users
                </h2>
                {oemPartner && (
                  <small className="text-muted">{oemPartner.email}</small>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="table-section">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div className="d-flex gap-3">
              <div className="position-relative">
                <input
                  type="search"
                  placeholder="Search by Name & Email..."
                  className="form-input"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ width: "200px" }}
                />
                {searchTerm === "" && (
                  <div
                    className="position-absolute"
                    style={{
                      top: "50%",
                      right: "13px",
                      color: "#666",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <FaSearch />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="table-scroll-wrapper">
            <table className="partners-table">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Domain</th>
                  <th>Verified</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {oemUsersLoading ? (
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#666",
                      }}
                    >
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading end users...</p>
                      </div>
                    </td>
                  </tr>
                ) : oemUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No end users found
                    </td>
                  </tr>
                ) : (
                  oemUsers
                    .filter((user) => user && user.email)
                    .map((user, index) => (
                      <tr key={user._id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.firstName || user.lastName || "N/A"}
                        </td>
                        <td>{user.email || "N/A"}</td>
                        <td>{user.domain || "N/A"}</td>
                        <td>
                          <span
                            className={`badge ${
                              user.verified ? "bg-success" : "bg-warning"
                            }`}
                          >
                            {user.verified ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              user.status === "active"
                                ? "bg-success"
                                : "bg-secondary"
                            }`}
                          >
                            {user.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={oemUsersPagination.totalPages}
            totalItems={oemUsersPagination.totalItems}
            itemsPerPage={oemUsersPagination.itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default ItEndUserOfOem;
